import { datadogRum } from '@datadog/browser-rum-slim';
import { aggregateAppLoadingEvents, aggregateDatadogEvents, getDatadogCurrentViewId, } from './aggregation';
import { CUSTOM_EVENT_CSS_LOAD_FAILURE, CUSTOM_EVENT_ERROR_PAGE_HIT, DATA_PROP_FEATURE_NAME, DATA_PROP_NAME_VIEW_DEPTH, GLOBAL_DATA_MARKS_TO_WAIT_FOR_IF_AUTHED, LISTENER_CLASS_FEATURE_SPECIFIC_DFL, MARK_NAME_ERROR_PAGE_HIT, MARK_NAME_VIEW_CHANGED, MARK_NAME_VIEW_DATA_FULLY_LOADED, MARK_NAME_VIEW_INITIAL_RENDER, } from './constants';
import { clearMarksMadeListenerByClass, markMeasurement, markTime, whenAllMarksMade, } from './markers';
import { getDeviceLoadCount, getLocalStorageItem, isBrowserSus } from './utils';
export { markMeasurement, markTime };
const datadogEnabled = !!process.env.DISABLE_DATADOG_RUM !== true && !window.Cypress;
let viewsDepth = 0;
let latestViewChangeMark;
// for live debugging in the browser
// @ts-expect-error
window._rum = window._rum || {};
// @ts-expect-error
window._rum.waiters = [];
export function initRUM({ applicationId, clientToken, service, version, env, getCurrentFeatureName, getFeatureConfig, }) {
    if (datadogEnabled &&
        applicationId &&
        clientToken &&
        service &&
        !window.Cypress) {
        datadogRum.init({
            applicationId,
            clientToken,
            site: 'datadoghq.com',
            service,
            version,
            env,
            sampleRate: 100,
            trackInteractions: false,
            trackViewsManually: !!getFeatureConfig,
            beforeSend: (event, context) => {
                var _a;
                // CSP reports are already being sent to DD via the report-uri/to locations
                // set in the CSP header itself. Given that, double sending it for RUM
                // doesn't add value.
                if (event.type === 'error' && ((_a = event.error) === null || _a === void 0 ? void 0 : _a.source) === 'report') {
                    return false;
                }
                aggregateDatadogEvents({
                    event,
                    context,
                    getCurrentFeatureName,
                });
            },
        });
        datadogRum.addRumGlobalContext('suspiciousUsage', isBrowserSus);
        datadogRum.addTiming('deviceLoadCount', getDeviceLoadCount());
        if (getFeatureConfig) {
            aggregateAppLoadingEvents({ getFeatureConfig });
            // Check to measure how often users are failing to load
            // the CSS files.
            const appCSS = document.getElementById('app-styles');
            // @ts-expect-error
            if (appCSS && appCSS.failedToLoad === true) {
                datadogRum.addAction(CUSTOM_EVENT_CSS_LOAD_FAILURE, {
                    cssLocation: appCSS.href,
                });
            }
        }
    }
}
/**
 * This function provides view timing marker functions that are bound to the same view. Allowing
 * for developers not to have to worry about asserting that the view is still active when marking it.
 *
 * When marking times for views, the functions such as rendering and loading data
 * are tied to one another but they can very wildly in when they actually happen. Other
 * interactions can happen that trigger the same events (even on the same route) before
 * a prior function + mark completed. For example, while loading data for View A, the user
 * might navigate to View B (and even back to A). When this happens the loading data for
 * the views should only be associated with the navigation/viewId that it was triggered on.
 *
 * @returns an object with methods that can be used to mark times for the same view
 */
export function getViewMarkFns({ featureConfig }) {
    const featureName = featureConfig.name;
    const currentViewId = getDatadogCurrentViewId();
    const viewIsInitialLoad = isInitialLoad();
    let markedIR = false;
    let markedDFL = false;
    return {
        markInitialRender: (additionalProps) => {
            // allow multiple invocations but only one mark
            if (markedIR) {
                return;
            }
            markedIR = true;
            markTime(MARK_NAME_VIEW_INITIAL_RENDER, {
                viewId: currentViewId,
                featureName,
                ...(additionalProps || {}),
            });
        },
        markDataFullyLoaded: (additionalProps) => {
            // allow multiple invocations but only one mark
            if (markedDFL) {
                return;
            }
            markedDFL = true;
            markTime(MARK_NAME_VIEW_DATA_FULLY_LOADED, {
                viewId: currentViewId,
                featureName,
                ...(additionalProps || {}),
            });
        },
        whenDataFullyLoaded: ({ whenDFLCallback, timeout = 15000 }) => {
            var _a, _b, _c, _d, _e;
            // view marks fn is feature specific so calling this method
            // while DFL is already being monitored for another feature should
            // clear the pending listeners.
            clearMarksMadeListenerByClass(LISTENER_CLASS_FEATURE_SPECIFIC_DFL);
            let apiMarksToWaitFor = [];
            const invocationArgs = {
                viewDFLTimedOut: false,
            };
            // there are known global api calls that need to be made under certain scenarios
            // this is the logic that will determine the scenarios to add the global watching logic.
            if (viewIsInitialLoad) {
                const authKey = getLocalStorageItem('nf-session');
                // if a auth session is present we know the app will try to start
                // loading the user information
                if (authKey) {
                    apiMarksToWaitFor = [
                        ...apiMarksToWaitFor,
                        ...GLOBAL_DATA_MARKS_TO_WAIT_FOR_IF_AUTHED,
                    ];
                }
            }
            // grab all of the feature configured api rules that were flagged
            // as critical path data for the feature.
            if ((_a = featureConfig === null || featureConfig === void 0 ? void 0 : featureConfig.networkingRules) === null || _a === void 0 ? void 0 : _a.criticalPathAPICalls) {
                if (Array.isArray((_b = featureConfig === null || featureConfig === void 0 ? void 0 : featureConfig.networkingRules) === null || _b === void 0 ? void 0 : _b.criticalPathAPICalls)) {
                    apiMarksToWaitFor = [
                        ...apiMarksToWaitFor,
                        ...(_c = featureConfig === null || featureConfig === void 0 ? void 0 : featureConfig.networkingRules) === null || _c === void 0 ? void 0 : _c.criticalPathAPICalls,
                    ];
                }
                else if (typeof ((_d = featureConfig === null || featureConfig === void 0 ? void 0 : featureConfig.networkingRules) === null || _d === void 0 ? void 0 : _d.criticalPathAPICalls) ===
                    'function') {
                    apiMarksToWaitFor = [
                        ...apiMarksToWaitFor,
                        ...(_e = featureConfig === null || featureConfig === void 0 ? void 0 : featureConfig.networkingRules) === null || _e === void 0 ? void 0 : _e.criticalPathAPICalls(),
                    ];
                }
            }
            // @ts-expect-error
            window._rum.waiters.push({
                featureName,
                apiMarksToWaitFor,
            });
            // there are no pending api calls needing to be made
            // immediately invoke the callback
            if (apiMarksToWaitFor.length === 0) {
                whenDFLCallback === null || whenDFLCallback === void 0 ? void 0 : whenDFLCallback(invocationArgs);
                return;
            }
            // in cases of poor configuration or completely slowed down
            // experiences, we prevent waiting indefinitely by timing out.
            // Without this we can introduce unnecessary survivor bias into the perf monitoring
            // by only tracking values always resolve.
            const dflWaitTimeout = setTimeout(() => {
                invocationArgs.viewDFLTimedOut = true;
                whenDFLCallback === null || whenDFLCallback === void 0 ? void 0 : whenDFLCallback(invocationArgs);
                whenDFLCallback = null;
            }, timeout);
            whenAllMarksMade({
                listenerClass: LISTENER_CLASS_FEATURE_SPECIFIC_DFL,
                markNamesToWaitFor: apiMarksToWaitFor.map((apiName) => `api-${apiName}-complete`),
                startOffset: viewIsInitialLoad ? 0 : latestViewChangeMark === null || latestViewChangeMark === void 0 ? void 0 : latestViewChangeMark.index,
                whenMadeCallback: () => {
                    clearTimeout(dflWaitTimeout);
                    whenDFLCallback === null || whenDFLCallback === void 0 ? void 0 : whenDFLCallback(invocationArgs);
                    whenDFLCallback = null;
                },
            });
        },
    };
}
export function addGlobalContext(context, values) {
    if (datadogEnabled) {
        datadogRum.addRumGlobalContext(context, values);
    }
}
export function addUserContext({ id, plan, accountId, email }) {
    if (datadogEnabled) {
        const ddUserDetails = {
            id,
            plan,
            accountId,
        };
        if (typeof email === 'string') {
            // from a PII perspective, we don't share the email with DD.
            // Instead we create the information we'd like to infer from this.
            ddUserDetails.isInternalUser = email.endsWith('@netlify.com');
        }
        datadogRum.setUser(ddUserDetails);
    }
}
export function trackView(urlTrackPath, featureName) {
    if (datadogEnabled) {
        viewsDepth++;
        // set this context immediately before telling DD the view changed
        datadogRum.addRumGlobalContext(DATA_PROP_FEATURE_NAME, featureName);
        datadogRum.startView(urlTrackPath);
        latestViewChangeMark = markTime(MARK_NAME_VIEW_CHANGED, {
            urlTrackPath,
            featureName,
        });
        markTime(MARK_NAME_VIEW_CHANGED, { urlTrackPath, featureName });
        // tracking of how many routes the user has navigated to so far
        // (this helps understand perf of cache)
        datadogRum.addTiming(DATA_PROP_NAME_VIEW_DEPTH, viewsDepth);
    }
}
export function trackErrorPage(type) {
    if (datadogEnabled) {
        // Identify how the user navigated to this location. Did they
        // directly open a url (or enter it in address bar) or was this
        // an in-app navigation link.
        let navType = 'internal-nav';
        if (isInitialLoad()) {
            navType = 'page-load-nav';
        }
        const eventPayload = {
            pathname: window.location.pathname,
            errorPageType: type,
            navType,
        };
        datadogRum.addAction(CUSTOM_EVENT_ERROR_PAGE_HIT, eventPayload);
        markTime(MARK_NAME_ERROR_PAGE_HIT, eventPayload);
    }
}
const isInitialLoad = () => viewsDepth <= 1;
//# sourceMappingURL=index.js.map